import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NotificationSinkService, WellKnownNotificationHubUrls } from 'app/core/notifications';
import { mapJsonObjectToClient, mapJsonObjectToServer } from 'app/core/services';
import { Message } from 'app/layout/common/messages/messages.types';
import { environment } from 'environments/environment';
import { Observable, ReplaySubject, concatMap, delay, from, map, of, shareReplay, switchMap, take, tap } from 'rxjs';

export interface FindMessageResponse {
  allUsers: boolean;
  email?: string;
  userGuid: string;
  message: Message;
}
export interface ListMessagesForUserResponse {
  allUsers: boolean;
  email?: string;
  userGuid: string;
  messages: Message[];
}

@Injectable({providedIn: 'root'})
export class MessagesService
{
    private readonly messagesRequestUrl = `${environment.apiRoot}/Message/Messages`;
    private readonly updateRequestUrl = `${environment.apiRoot}/Message/Update`;
    private readonly deleteRequestUrl = `${environment.apiRoot}/Message/Delete`;
    private readonly markAllAsReadRequestUrl = `${environment.apiRoot}/Message/MarkAllAsRead`;
    private readonly adminSendMessageRequestUrl = `${environment.apiRoot}/Message/AdminSendMessage`;
    private readonly adminFindMessageRequestUrl = `${environment.apiRoot}/Message/AdminFindMessage`;
    private readonly adminListMessagesForUserRequestUrl = `${environment.apiRoot}/Message/AdminListMessagesForUser`;
    private readonly adminUpdateMessageRequestUrl = `${environment.apiRoot}/Message/AdminUpdateMessage`;
    private readonly adminDeleteMessageRequestUrl = `${environment.apiRoot}/Message/AdminDeleteMessage`;

    private utcNow(): Date {
      const now = new Date();
      return new Date(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds(),
        now.getUTCMilliseconds()
      );
    }

    private _messages: ReplaySubject<Message[]> = new ReplaySubject<Message[]>(1);
    private _messagesFiltered: Observable<Message[]> = this._messages.pipe(
      switchMap((messages) => {
        function filterMessages(messages: Message[]): Message[] {
          const now = this.utcNow();
          console.log('Messages received ' + now);
          return messages.filter((msg) => 
            (!msg.effectiveFrom || msg.effectiveFrom <= now) &&
            (!msg.expiry || msg.expiry > now)
          );
        }

        const now = this.utcNow();
        const delaySet = new Set<number>(messages
          .flatMap((msg) => [msg.effectiveFrom, msg.expiry])
          .filter((ts) => !!ts)
          .filter((ts) => ts > now)
          .map((ts) => Math.ceil(ts.valueOf() - now.valueOf()))
        );
        delaySet.add(0);

        const delayTotals = Array.from(delaySet).sort((a, b) => a - b);
        const delays = delayTotals.map((d, i) => i > 0 ? d - delayTotals[i - 1] : d);


        if (delays.length > 1) {
          console.log('Message reprocessing delays: ' + delays.map((ms) => ms + 'ms'));
        }

        return from(delays).pipe(
          concatMap((ms) => of(void 0).pipe(delay(ms), map(() => filterMessages.call(this, messages))))
        );
      }),
      shareReplay(1),
    );

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient, notificationSink: NotificationSinkService)
    {
      // Listen for OnMessage push notification and refresh messages
      notificationSink.create(WellKnownNotificationHubUrls.eventHub).on$('OnMessage', (observer) => observer.next() /* Update on reconnect */).subscribe(() => this.getAll().subscribe());
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for messages
     */
    get messages$(): Observable<Message[]>
    {
        return this._messagesFiltered;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all messages
     */
    getAll(): Observable<Message[]>
    {
        return this._httpClient.get<Message[]>(this.messagesRequestUrl).pipe(
            tap((messages) => {
                this._messages.next(mapJsonObjectToClient(messages));
            }),
        );
    }

    /**
     * Create a message
     *
     * @param message
     */
    // create(message: Message): Observable<Message>
    // {
    //     return this.messages$.pipe(
    //         take(1),
    //         switchMap(messages => this._httpClient.post<Message>('api/common/messages', {message}).pipe(
    //             map((newMessage) => {
    //                 // Update the messages with the new message
    //                 this._messages.next([...messages, newMessage]);

    //                 // Return the new message from observable
    //                 return newMessage;
    //             })
    //         ))
    //     );
    // }

    /**
     * Update the message
     *
     * @param id
     * @param message
     */
    update(id: string, message: Message): Observable<Message>
    {
        const params = mapJsonObjectToServer({
            id,
            message
        });
        return this.messages$.pipe(
            take(1),
            switchMap(messages => this._httpClient.patch<Message>(this.updateRequestUrl, params).pipe(
                map((updatedMessage: Message) => {
                    if (updatedMessage) {
                        updatedMessage = mapJsonObjectToClient(updatedMessage);
                        // Find the index of the updated message
                        const index = messages.findIndex(item => item.id === id);

                        // Update the message
                        messages[index] = updatedMessage;

                        // Update the messages
                        this._messages.next(messages);
                    }

                    // Return the updated message
                    return updatedMessage;
                }),
            )),
        );
    }

    /**
     * Send a new message to a user or all users
     * SaaS admin user only
     *
     * @param message
     */
    adminSendMessage(message: Message, email?: string): Observable<Message>
    {
        const params = mapJsonObjectToServer({
          ...message,
          email,
        });
        return this._httpClient.post<Message>(this.adminSendMessageRequestUrl, params);
    }

    /**
     * Get a message for a user or all users
     * SaaS admin user only
     *
     * @param userGuidOrEmail
     * @param messageUid
     */
    adminFindMessage(allUsers: boolean, userGuidOrEmail: string, messageUid: string): Observable<FindMessageResponse>
    {
        const params = {
          allUsers,
          userGuidOrEmail,
          messageUid,
        };
        return this._httpClient.get<FindMessageResponse>(this.adminFindMessageRequestUrl, { params })
          .pipe(map((response: FindMessageResponse) => mapJsonObjectToClient(response)));
    }

    /**
     * Get all messages for a userGuid or all users
     * SaaS admin user only
     *
     * @param allUsers
     * @param userGuidOrEmail
     */
    adminListMessagesForUser(allUsers: boolean, userGuidOrEmail?: string): Observable<ListMessagesForUserResponse>
    {
        const params = {
          allUsers,
          userGuidOrEmail,
        };
        return this._httpClient.get<ListMessagesForUserResponse>(this.adminListMessagesForUserRequestUrl, { params })
          .pipe(map((response: ListMessagesForUserResponse) => mapJsonObjectToClient(response)));
    }

    /**
     * Update a message for a user or all users
     * SaaS admin user only
     * 
     * @param message
     */
    adminUpdateMessage(userGuid: string, message: Message): Observable<Message>
    {
      const params = mapJsonObjectToServer({
        ...message,
        userId: userGuid,
      });
      return this._httpClient.patch<Message>(this.adminUpdateMessageRequestUrl, params)
        .pipe(map((message: Message) => mapJsonObjectToClient(message)));
    }

    /**
     * Delete a message for a user or all users
     * SaaS admin user only
     * 
     * @param message
     */
    adminDeleteMessage(userGuid: string, messageUid: string): Observable<boolean>
    {
      const params = {
        userGuid,
        messageUid,
      };
      return this._httpClient.delete<boolean>(this.adminDeleteMessageRequestUrl, { params });
    }

    /**
     * Delete the message
     *
     * @param id
     */
    delete(id: string): Observable<boolean>
    {
        return this.messages$.pipe(
            take(1),
            switchMap(messages => this._httpClient.delete<boolean>(this.deleteRequestUrl, {params: {id}}).pipe(
                map((isDeleted: boolean) =>
                {
                    // Find the index of the deleted message
                    const index = messages.findIndex(item => item.id === id);

                    // Delete the message
                    messages.splice(index, 1);

                    // Update the messages
                    this._messages.next(messages);

                    // Return the deleted status
                    return isDeleted;
                }),
            )),
        );
    }

    /**
     * Mark all messages as read
     */
    markAllAsRead(): Observable<boolean>
    {
        return this.messages$.pipe(
            take(1),
            switchMap(messages => this._httpClient.get<boolean>(this.markAllAsReadRequestUrl).pipe(
                map((isUpdated: boolean) =>
                {
                    // Go through all messages and set them as read
                    messages.forEach((message, index) =>
                    {
                        messages[index].read = true;
                    });

                    // Update the messages
                    this._messages.next(messages);

                    // Return the updated status
                    return isUpdated;
                }),
            )),
        );
    }
}
